<template>
    <div>
        <PanelPage v-if="currentStep === 1">
            <template v-slot:title>
                <SmartBreadcrumbs
                    type="InertiaLink"
                    :pages="[
                        {
                            name: $t('messages.dashboard'),
                            href: meta.links.dashboard_index,
                        },
                        {
                            name: $tc('messages.students', 2),
                            href: meta.links.index,
                        },
                        {
                            name: $t('messages.import'),
                            href: null,
                        },
                    ]"
                />
            </template>

            <div class="md:grid md:grid-cols-3 md:gap-6">
                <div class="col-span-2">
                    <iframe
                        width="560"
                        height="315"
                        src="https://www.youtube-nocookie.com/embed/yqAjyN0JpjU"
                        title="YouTube video player"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowfullscreen
                    ></iframe>
                </div>
                <div class="flex justify-end">
                    <div>
                        <AlertMessage class="mt-5 inline-block" type="info">
                            <template v-slot:title>
                                {{ $t('messages.warning') }}
                            </template>
                            <template v-slot:body>
                                <AppButton :href="meta.links.csv_template" target="_blank">
                                    {{ $t('messages.template') }}
                                </AppButton>
                            </template>
                        </AlertMessage>
                    </div>
                </div>
            </div>

            <form :action="meta.links.store" method="post" @submit.prevent="submit" class="mt-3">
                <div>
                    <VueCsvImport v-model="form.csv" :fields="mapFields" :text="textTranslations">
                        <!--                        button-class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"-->
                        <VueCsvToggleHeaders v-slot="{ hasHeaders, toggle }">
                            <div class="hidden">
                                ({{ hasHeaders }})
                                <button @click.prevent="toggle">Has Headers</button>
                            </div>
                        </VueCsvToggleHeaders>
                        <VueCsvErrors />
                        <VueCsvInput />
                        <VueCsvMap
                            auto-match
                            auto-match-ignore-case
                            :select-attributes="{
                                class: 'mt-1 block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md',
                            }"
                        />
                        <template v-slot:next v-slot="{ load }">
                            <AppButton element="button" type="button" @click.prevent="load" class="mt-5">
                                {{ $t('messages.next') }}
                            </AppButton>
                        </template>
                    </VueCsvImport>

                    <div class="flex justify-end mt-5">
                        <AppButton
                            element="button"
                            type="submit"
                            :desired-action="!(formIncomplete || form.processing)"
                        >
                            {{ $t('messages.next') }}
                            <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                        </AppButton>
                    </div>
                    <div v-if="formIncomplete" class="flex justify-end mt-1">
                        {{ $t('messages.please_select_all_columns') }}
                    </div>
                </div>
            </form>
        </PanelPage>

        <div v-if="currentStep === 2" class="pt-2 md:pt-6">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                <h1 class="text-2xl font-semibold text-gray-900">
                    <SmartBreadcrumbs
                        type="a"
                        :pages="[
                            {
                                name: $tc('messages.students', 2),
                                href: meta.links.index,
                            },
                            {
                                name: $t('messages.import'),
                                href: null,
                            },
                        ]"
                    />
                </h1>
            </div>

            <div>
                <form :action="meta.links.store" @submit.prevent="submit2" id="form-step-2" class="absolute">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="sticky top-0 bg-gray-50 z-10">
                            <tr class="bg-gray-50">
                                <th colspan="15">
                                    <div class="flex">
                                        <AppButton
                                            class="sticky top-0 left-0 m-1"
                                            element="button"
                                            type="submit"
                                            :disabled="form2.processing"
                                            form="form-step-2"
                                            desired-action
                                        >
                                            {{ $t('messages.save') }}
                                            <span v-if="form2.processing" class="ml-1 btn-spinner" />
                                        </AppButton>
                                    </div>
                                </th>
                            </tr>
                            <tr>
                                <th
                                    scope="col"
                                    class="sticky left-0 max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.first_name') }}
                                </th>
                                <th
                                    scope="col"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.nickname') }}
                                </th>
                                <th
                                    scope="col"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.last_name') }}
                                </th>
                                <th
                                    scope="col"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.second_last_name') }}
                                </th>
                                <th
                                    scope="col"
                                    class="sticky-top-0 left-0 z-10 max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.school_government_id') }}
                                </th>
                                <th
                                    scope="col"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.date_of_birth') }}
                                </th>
                                <th
                                    scope="col"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.email') }}
                                </th>
                                <th
                                    scope="col"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.gender') }}
                                </th>
                                <th
                                    scope="col"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.mother_tongue') }}
                                </th>
                                <th
                                    scope="col"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.nationality') }}
                                </th>
                                <th
                                    scope="col"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.country_of_origin') }}
                                </th>
                                <th
                                    scope="col"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.password') }}
                                </th>
                                <th
                                    scope="col"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.grade_level') }}
                                </th>
                                <th
                                    scope="col"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.letter') }}
                                </th>
                                <th
                                    scope="col"
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(student, index) in form2.students"
                                :key="index"
                            >
                                <td
                                    class="sticky left-0 bg-gray-50 max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <div class="flex space-x-1 items-center">
                                        <button
                                            type="button"
                                            @click="deleteRow(index)"
                                            :title="$t('messages.delete')"
                                            class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-200 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                    clip-rule="evenodd"
                                                ></path>
                                            </svg>
                                        </button>
                                        <FormInput
                                            :id="`first_name_${index}`"
                                            v-model.trim="student.first_name"
                                            name="first_name"
                                            type="text"
                                            max="60"
                                            class="mt-1"
                                        />
                                    </div>
                                    <FormError :value="form2.errors[`students.${index}.first_name`]" class="mt-2" />
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <FormInput
                                        :id="`name_${index}`"
                                        v-model.trim="student.name"
                                        name="name"
                                        type="text"
                                        max="60"
                                        :placeholder="$t('messages.(optional)')"
                                        class="mt-1"
                                    />
                                    <FormError :value="form2.errors[`students.${index}.name`]" class="mt-2" />
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <FormInput
                                        :id="`last_name_${index}`"
                                        v-model.trim="student.last_name"
                                        name="last_name"
                                        type="text"
                                        max="60"
                                        class="mt-1"
                                    />
                                    <FormError :value="form2.errors[`students.${index}.last_name`]" class="mt-2" />
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <FormInput
                                        :id="`second_last_name_${index}`"
                                        v-model.trim="student.second_last_name"
                                        name="second_last_name"
                                        type="text"
                                        max="60"
                                        :placeholder="$t('messages.(optional)')"
                                        class="mt-1"
                                    />
                                    <FormError
                                        :value="form2.errors[`students.${index}.second_last_name`]"
                                        class="mt-2"
                                    />
                                </td>
                                <td
                                    class="sticky-top-0 left-0 z-10 max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <FormInput
                                        :id="`school_government_id_${index}`"
                                        v-model.trim="student.school_government_id"
                                        name="school_government_id"
                                        type="text"
                                        max="60"
                                        :placeholder="$t('messages.(optional)')"
                                        class="mt-1"
                                    />
                                    <FormError
                                        :value="form2.errors[`students.${index}.school_government_id`]"
                                        class="mt-2"
                                    />
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <FormInput
                                        :id="`date_of_birth_${index}`"
                                        type="date"
                                        v-model.trim="student.date_of_birth"
                                        name="date_of_birth"
                                        class="mt-1"
                                    />
                                    <FormError :value="form2.errors[`students.${index}.date_of_birth`]" class="mt-2" />
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <FormInput
                                        :id="`email_${index}`"
                                        v-model.trim="student.email"
                                        name="email"
                                        type="text"
                                        max="60"
                                        class="mt-1"
                                    />
                                    <FormError :value="form2.errors[`students.${index}.email`]" class="mt-2" />
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <FormSelect
                                        required
                                        :id="`gender_${index}`"
                                        v-model="student.gender"
                                        name="gender"
                                        :options="options.genders"
                                        class="mt-1"
                                    />
                                    <FormError :value="form2.errors[`students.${index}.gender`]" class="mt-2" />
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <FormSelect
                                        :id="`mother_tongue_${index}`"
                                        v-model="student.mother_tongue"
                                        :options="options.languages"
                                        optgroups
                                        class="mt-1"
                                    />
                                    <FormError :value="form2.errors[`students.${index}.mother_tongue`]" class="mt-2" />
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <FormSelect
                                        :id="`nationality_${index}`"
                                        v-model="student.nationality"
                                        name="nationality"
                                        :options="options.countries"
                                        optgroups
                                        class="mt-1"
                                    />
                                    <FormError :value="form2.errors[`students.${index}.nationality`]" class="mt-2" />
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <FormSelect
                                        :id="`country_of_origin_${index}`"
                                        v-model="student.country_of_origin"
                                        name="country_of_origin"
                                        :options="options.countries"
                                        optgroups
                                        class="mt-1"
                                    />
                                    <FormError
                                        :value="form2.errors[`students.${index}.country_of_origin`]"
                                        class="mt-2"
                                    />
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <FormInput
                                        :id="`password_${index}`"
                                        v-model.trim="student.password"
                                        name="password"
                                        type="text"
                                        max="60"
                                        class="mt-1"
                                    />
                                    <FormError :value="form2.errors[`students.${index}.password`]" class="mt-2" />
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <select
                                        required
                                        v-model="student.grade_level_id"
                                        class="mt-1 block py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    >
                                        <option value="">
                                            {{ $t('messages.select') }}
                                        </option>
                                        <option
                                            v-for="gradeLevel in options.gradeLevels"
                                            :value="gradeLevel.id"
                                            :key="gradeLevel.id"
                                        >
                                            {{ gradeLevel.name }}
                                        </option>
                                    </select>
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <select
                                        v-model="student.letter"
                                        required
                                        class="w-12 mt-1 block py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    >
                                        <option
                                            v-for="letter in ['A', 'B', 'C', 'D', 'E', 'F', 'G']"
                                            :value="letter"
                                            :key="letter"
                                        >
                                            {{ letter }}
                                        </option>
                                    </select>
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                ></td>
                            </tr>
                        </tbody>
                    </table>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import AdminLayout from '@/Shared/AdminLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import { VueCsvErrors, VueCsvImport, VueCsvInput, VueCsvMap, VueCsvToggleHeaders } from 'vue-csv-import';
import AppButton from '@/components/AppButton.vue';
import AlertMessage from '@/components/AlertMessage.vue';

export default {
    components: {
        PanelPage,
        FormInput,
        FormError,
        FormSelect,
        VueCsvImport,
        VueCsvToggleHeaders,
        VueCsvErrors,
        VueCsvInput,
        VueCsvMap,
        AppButton,
        AlertMessage,
    },
    layout: AdminLayout,
    name: 'Create',
    props: ['meta', 'step', 'students', 'options'],
    created() {
        this.currentStep = this.step;
    },
    data() {
        return {
            currentStep: 1,
            form: this.$inertia.form({
                csv: null,
            }),
            form2: this.$inertia.form({
                students: this.students,
            }),
            textTranslations: {
                // errors: {
                //     fileRequired: 'A file is required',
                //     invalidMimeType: 'Invalid file type',
                // },
                // toggleHeaders: 'File has headers',
                submitBtn: this.$t('messages.continue'),
                fieldColumn: this.$t('messages.field'),
                csvColumn: this.$t('messages.column'),
            },
            mapFields: {
                school_government_id: { label: this.$t('messages.school_government_id'), required: false },
                first_name: { label: this.$t('messages.first_name'), required: true },
                name: { label: this.$t('messages.nickname'), required: false },
                last_name: { label: this.$t('messages.last_name'), required: true },
                second_last_name: { label: this.$t('messages.second_last_name'), required: true },
                date_of_birth: { label: this.$t('messages.date_of_birth'), required: true },
                email: { label: this.$t('messages.email'), required: true },
                gender: { label: this.$t('messages.gender'), required: true },
                mother_tongue: { label: this.$t('messages.mother_tongue'), required: false },
                nationality: { label: this.$t('messages.nationality'), required: false },
                country_of_origin: { label: this.$t('messages.country_of_origin'), required: false },
                password: { label: this.$t('messages.password'), required: false },
                group_id: { label: this.$t('messages.group'), required: true },
            },
        };
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.csv, {
                onFinish: () => {
                    if (this.step === 2) {
                        this.currentStep = 2;
                        this.form2.students = this.students;
                    }
                },
            });
        },
        submit2() {
            this.form2.post(this.meta.links.store, {
                onError: () => {
                    if (this.form2.hasErrors) {
                        this.currentStep = 2;
                    }
                },
            });
        },
        deleteRow(index) {
            this.form2.clearErrors();
            this.form2.students.splice(index, 1);
        },
    },
    computed: {
        formIncomplete() {
            return this.form.csv === null;
        },
    },
};
</script>
